import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, ArrowLeft, Info, Sparkles, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES } from "../lib/i18n";

const RATE_PER_SQM = {
  restaurant: { std: 3200, prem: 8500 },
  cafe: { std: 2200, prem: 5200 },
  hotel: { std: 4500, prem: 12000 },
  resort: { std: 4800, prem: 13500 },
  villa_palace: { std: 6500, prem: 18000 },
  hospital: { std: 3800, prem: 9500 },
  bakery: { std: 3000, prem: 7500 },
  laundry: { std: 2800, prem: 7000 },
  catering: { std: 3500, prem: 8500 },
  supermarket: { std: 3600, prem: 8000 },
  cloud_kitchen: { std: 2500, prem: 6000 },
  central_kitchen: { std: 4200, prem: 11000 },
};

const SIZES = [
  { key: "xs", label_en: "Under 40 m²", label_ar: "أقل من 40 م²", area: 30 },
  { key: "s",  label_en: "40 – 80 m²",  label_ar: "40 – 80 م²",  area: 60 },
  { key: "m",  label_en: "80 – 150 m²", label_ar: "80 – 150 م²", area: 115 },
  { key: "l",  label_en: "150 – 300 m²", label_ar: "150 – 300 م²", area: 225 },
  { key: "xl", label_en: "Over 300 m²",  label_ar: "أكثر من 300 م²", area: 400 },
];

const LEVELS = [
  { key: "std",  label_en: "Standard",  label_ar: "قياسي", note_en: "Reliable, cost-effective", note_ar: "موثوق واقتصادي" },
  { key: "prem", label_en: "Premium",   label_ar: "بريميوم", note_en: "Top brands, longer warranty", note_ar: "علامات فاخرة، ضمان أطول" },
];

const formatAED = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(Math.round(n));
};

export function CostEstimator() {
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const [sector, setSector] = useState("restaurant");
  const [size, setSize] = useState("m");
  const [level, setLevel] = useState("std");

  const estimate = useMemo(() => {
    const sizeObj = SIZES.find((s) => s.key === size) || SIZES[2];
    const rate = RATE_PER_SQM[sector] || RATE_PER_SQM.restaurant;
    const base = rate[level];
    const min = Math.round(base * sizeObj.area * 0.85);
    const max = Math.round(base * sizeObj.area * 1.15);
    return { min, max, area: sizeObj.area, mid: Math.round((min + max) / 2) };
  }, [sector, size, level]);

  return (
    <section
      className="py-24 bg-gradient-to-b from-white via-cyan-50/40 to-white relative overflow-hidden"
      data-testid="cost-estimator"
    >
      {/* Decorative blur circles */}
      <div className="absolute top-16 end-16 w-72 h-72 rounded-full bg-[#ef4444]/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-16 start-16 w-72 h-72 rounded-full bg-[#0891b2]/8 blur-3xl pointer-events-none" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-[#ef4444]/20 text-[#ef4444] rounded-full px-4 py-1.5 text-[10px] tracking-[0.3em] font-semibold mb-4 uppercase shadow-sm">
            <Sparkles className="w-3.5 h-3.5" />
            {t.estimator.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-4 leading-[1.1]">
            {t.estimator.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">{t.estimator.subtitle}</p>
        </div>

        {/* Calculator card - full width, lighter */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="grid lg:grid-cols-5">
            {/* LEFT: Inputs (3 cols) */}
            <div className="lg:col-span-3 p-8 lg:p-10 border-b lg:border-b-0 lg:border-e border-slate-100 bg-white">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-11 h-11 rounded-xl bg-cyan-50 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-[#0891b2]" />
                </div>
                <div>
                  <div className="font-display text-lg text-[#0891b2]">{t.estimator.widgetTitle}</div>
                  <div className="text-slate-500 text-xs">{t.estimator.widgetHint}</div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Sector */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                    {t.estimator.sectorLabel}
                  </label>
                  <Select value={sector} onValueChange={setSector}>
                    <SelectTrigger data-testid="est-sector" className="h-12 bg-slate-50 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t.categories[c]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Size buttons */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                    {t.estimator.sizeLabel}
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {SIZES.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`est-size-${s.key}`}
                        onClick={() => setSize(s.key)}
                        className={`text-[11px] py-3 px-1 rounded-xl font-medium transition-all border-2 ${
                          size === s.key
                            ? "bg-cyan-50 text-[#0891b2] border-[#0891b2]"
                            : "bg-white text-slate-500 border-slate-200 hover:border-blue-200 hover:text-[#0891b2]"
                        }`}
                      >
                        {lang === "ar" ? s.label_ar : s.label_en}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Level with descriptions */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                    {t.estimator.levelLabel}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {LEVELS.map((l) => {
                      const active = level === l.key;
                      return (
                        <button
                          key={l.key}
                          type="button"
                          data-testid={`est-level-${l.key}`}
                          onClick={() => setLevel(l.key)}
                          className={`relative text-start p-4 rounded-xl transition-all border-2 ${
                            active
                              ? "bg-red-50 border-[#ef4444]"
                              : "bg-white border-slate-200 hover:border-red-200"
                          }`}
                        >
                          {active && (
                            <div className="absolute top-3 end-3 w-5 h-5 rounded-full bg-[#ef4444] flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                          <div className={`font-display text-base mb-1 ${active ? "text-[#ef4444]" : "text-[#0891b2]"}`}>
                            {lang === "ar" ? l.label_ar : l.label_en}
                          </div>
                          <div className="text-xs text-slate-500 leading-snug">
                            {lang === "ar" ? l.note_ar : l.note_en}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Result (2 cols) */}
            <div className="lg:col-span-2 p-8 lg:p-10 bg-gradient-to-br from-cyan-50/60 to-red-50/40 flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
                  {t.estimator.resultLabel}
                </div>
                <div className="font-display text-4xl sm:text-5xl text-[#0891b2] mb-2 leading-none" data-testid="est-result">
                  <span dir="ltr">
                    {formatAED(estimate.min)} – {formatAED(estimate.max)}
                  </span>
                </div>
                <div className="text-[#ef4444] text-lg font-display mb-1" dir="ltr">AED</div>
                <div className="text-slate-500 text-xs mb-6">
                  {t.estimator.forApprox} <span className="font-semibold text-slate-700" dir="ltr">{estimate.area} m²</span>
                </div>

                {/* Bullets */}
                <div className="space-y-2 mb-6">
                  {t.estimator.bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <div className="w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-green-600" />
                      </div>
                      {b}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Link to="/quote">
                  <Button
                    data-testid="est-quote-cta"
                    className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 font-semibold shadow-md"
                  >
                    {t.estimator.cta} <Arrow className="w-4 h-4 ms-2" />
                  </Button>
                </Link>
                <div className="flex items-start gap-1.5 mt-3 text-[10px] text-slate-500 leading-relaxed">
                  <Info className="w-3 h-3 shrink-0 mt-0.5" />
                  {t.estimator.disclaimer}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
